import { ZodError } from 'zod';
import { env } from '@/env';
import { isAuthFailure } from '@/shared/auth-failure';
import { getAuthToken, redirectToLogin } from '@/shared/auth-token';
import { reportStorageModuleRefusal, STORAGE_MODULE_OFF_MESSAGE } from '@/shared/storage-module';
import { StorageLocationListSchema, StorageLocationSchema, type StorageLocationContract } from '@/contracts/storage-locations';

/**
 * API bindings for "Mis depósitos" (roadmap §2 "Ubicaciones (1 cuenta, N ubicaciones)", §4
 * Milestone 3): `GET/POST /storage/locations`, `PATCH/DELETE /storage/locations/:id`.
 *
 * DELIBERATELY SELF-CONTAINED for its own request/envelope/error logic — does not import a shared
 * `@/shared/api`-like module. This repo's milestones run as concurrent agents against the same
 * working tree with no file-level isolation between them; while this milestone was in progress,
 * more than one other concurrent milestone independently created its own generic HTTP-transport
 * module under the name this file would otherwise have used (`src/shared/api.ts`,
 * `src/shared/apiClient.ts` and `src/shared/storage-api.ts` all appeared and were rewritten
 * mid-task by different concurrent milestones — see `src/shared/storage-services-api.ts`'s own
 * docblock, Milestone 4, which hit the same thing first and names the same files). Depending on
 * any of those here would make this screen's correctness hostage to modules this milestone does
 * not own and that were still being written/renamed as this file was finalized. Once the agents
 * (or a human) reconcile on ONE shared transport module, this file's `request`/`ApiError`/
 * envelope-reading logic is a mechanical delete-and-reimport away from using it — see this
 * milestone's final report for the concurrency finding in full.
 *
 * Auth + error-envelope shape mirror `storage-services-api.ts` exactly (same source: this repo's
 * own AGENTS.md + nexvia-api-web's error middleware, `{success:false,message,code?}` — confirmed
 * against `src/shared/middlewares/error.middleware.ts` there). `message` travels in English; no
 * page shows it to the user directly — see `describeApiError` below.
 *
 * **Fixed 2026-10-14** (NEX-1164 follow-up): the Bearer token used to be read straight out of a
 * cached `localStorage[TOKEN_STORAGE_KEY]` mirror, refreshed only by `useAuth.tsx`'s
 * `onIdTokenChanged` listener — which depends on Firebase's own background timer having fired
 * recently, and does not while a tab is backgrounded or the machine is asleep. A long-backgrounded
 * tab sent the same expired token on every request to `/storage/*`, each answered **403** (this
 * backend's status for an expired Firebase token on these routes, not 401), with nothing reacting
 * to it. `request()` below now asks the shared `@/shared/auth-token` helper for the live SDK token
 * instead, and retries once with a forced refresh on a 401 *or* 403 before redirecting to `/login`
 * — the exact same fix, and the exact same shared helper, as `api.ts` (see that module's and
 * `auth-token.ts`'s own docblocks for the full root-cause writeup). Unlike the request/envelope
 * logic above, this one slice — the token read and the auth-retry — is intentionally shared, not
 * self-contained: it was byte-for-byte duplicated across all four transport modules before this
 * fix (documented as F-001 in `docs/TECHNICAL-AUDIT.md`), and that duplication is what let one copy
 * drift from the others in the first place.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function parseJsonSafely(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Some nexvia-api-web storage endpoints answer `{ success: true, data }` (storage.controller.ts's
 * profile/locations/services/requests/quotes handlers); `/locations/*` (locations-catalog.ts,
 * reusing this same request() via the exported apiGet below) answers a bare array/object instead
 * — confirmed by reading both real controllers, not assumed. Detected by shape rather than by
 * path: a body that actually carries BOTH `success` and `data` keys is unwrapped to `.data`;
 * anything else (a bare array included — `in` never matches on one) passes through unchanged, so
 * `/locations/departments`'s bare array keeps working exactly as before.
 */
function unwrapEnvelope<T>(body: unknown): T {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'success' in body && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

interface RawResponse {
  ok: boolean;
  status: number;
  body: unknown;
}

async function sendRequest(path: string, init: RequestInit | undefined, token: string | null): Promise<RawResponse> {
  // `new URL(path, base)` rather than plain concatenation: VITE_API_URL carries a trailing slash
  // (same convention as nexvia-web-app's own .env), and `path` starts with one too — string
  // concatenation produced a literal `//` that reached the network as part of the URL.
  const response = await fetch(new URL(path, env.VITE_API_URL).toString(), {
    ...init,
    headers: {
      Accept: 'application/json',
      // Only a JSON string: a `FormData` body (the photo upload) sets its own multipart boundary.
      ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers
    }
  });

  const body = parseJsonSafely(await response.text());
  return { ok: response.ok, status: response.status, body };
}

function toApiError(path: string, raw: RawResponse): ApiError {
  const record = raw.body && typeof raw.body === 'object' ? (raw.body as Record<string, unknown>) : null;
  const message = typeof record?.message === 'string' ? record.message : `Request to ${path} failed with status ${raw.status}`;
  const code = typeof record?.code === 'string' ? record.code : undefined;
  return new ApiError(message, raw.status, code);
}


/**
 * One retry, ever, per call — never a loop (NEX-1164 follow-up, 2026-10-14): mirrors `api.ts`'s own
 * retry-then-redirect logic exactly, reusing the SAME shared `getAuthToken`/`redirectToLogin` from
 * `@/shared/auth-token` rather than a second copy of that logic — see this file's own docblock, and
 * `auth-token.ts`'s, for the stale-token bug this fixes and the 403-not-401 gotcha on these routes.
 * A 401/403 forces a live token refresh (`getAuthToken(true)`) and replays the exact same request
 * once with whatever token that returns; anything still wrong after that single replay (no
 * refreshed token to retry with, or the replay is *also* 401/403) redirects to `/login` instead of
 * trying again, and throws the same `ApiError` shape this module has always thrown — only the
 * retry-then-redirect behavior around it, and where the token comes from, are new. A non-auth
 * failure (validation, 5xx, …) is never retried, exactly as before.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthToken();
  let raw = await sendRequest(path, init, token);

  // NEX-1164 — only a session failure (401, 403 AUTH_*) retries/redirects; a 403 domain refusal

  // (STORAGE_NOT_APPROVED, STORAGE_ROLE_REQUIRED, ...) falls through to the page as a plain ApiError.

  if (isAuthFailure(raw.status, raw.body)) {
    const refreshedToken = await getAuthToken(true);
    if (refreshedToken) {
      raw = await sendRequest(path, init, refreshedToken);
    }
    if (!refreshedToken || isAuthFailure(raw.status, raw.body)) {
      redirectToLogin();
      throw toApiError(path, raw);
    }
  }

  if (!raw.ok) {
    const error = toApiError(path, raw);
    reportStorageModuleRefusal(error.status, error.code);
    throw error;
  }

  return unwrapEnvelope<T>(raw.body);
}

export const apiGet = <T>(path: string): Promise<T> => request<T>(path, { method: 'GET' });
export const apiPost = <T>(path: string, payload: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: JSON.stringify(payload) });
export const apiPatch = <T>(path: string, payload: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(payload) });
export const apiDelete = <T = void>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' });

/**
 * Backend error messages are English and sometimes internal ("Failed to ...") — shown as-is they
 * would read as a mistranslated app. Same reasoning as `useAuth.tsx`'s `describeAuthError`: map to
 * a handful of hand-written Spanish messages instead of surfacing `error.message` directly.
 */
export function describeApiError(error: unknown): string {
  if (error instanceof ApiError) {
    const known = error.code ? LOCATION_ERROR_MESSAGES[error.code] : undefined;
    if (known) return known;
    // NEX-1164 — only a session failure reads as an expired session (`auth-failure.ts`, the rule the
    // transport itself retries on): a 403 with a domain code is the API refusing this account
    // something, and "log in again" would send the depósito in circles.
    if (isAuthFailure(error.status, error.code ? { code: error.code } : null)) return 'Tu sesión expiró. Volvé a iniciar sesión.';
    if (error.status === 403) return 'Tu cuenta de depósito no puede hacer esto. Revisá el estado de tu alta en Mi empresa.';
    if (error.status >= 400 && error.status < 500) return 'Revisá los datos e intentá de nuevo.';
  }
  return 'No pudimos conectarnos con el servidor. Intentá de nuevo en un momento.';
}

/** NEX-1164 — what `uploadStorageLocationPhoto` throws when the upload never got an answer (see there). */
const UPLOAD_NETWORK_ERROR_CODE = 'UPLOAD_NETWORK_ERROR';
const UPLOAD_NETWORK_ERROR_MESSAGE = 'No pudimos subir el archivo. Revisá tu conexión y probá de nuevo.';

/**
 * Codes the location endpoints answer (nexvia-api-web `storage-location.validators.ts`,
 * `update-storage-location.handler.ts`, `delete-storage-location.handler.ts`, `domain/photo-format.ts`,
 * `require-storage-profile.helper.ts`), plus this module's own upload code. Matched by code, never by
 * the English `message`.
 */
const LOCATION_ERROR_MESSAGES: Record<string, string> = {
  STORAGE_NOT_APPROVED:
    'Tu cuenta de depósito no está habilitada para esto. Revisá el estado de tu alta y tu documentación en Mi empresa.',
  STORAGE_ROLE_REQUIRED: 'Esta cuenta no es una cuenta de depósito.',
  // NEX-1165 — a location that backs an accepted quote, or a pending one on a published request.
  STORAGE_LOCATION_IN_USE:
    'No podés eliminar este depósito: tiene cotizaciones pendientes o aceptadas. Vas a poder eliminarlo cuando dejen de estarlo.',
  STORAGE_LOCATION_ZONE_IN_USE:
    'No podés cambiar el departamento o la localidad de este depósito mientras tenga cotizaciones pendientes o aceptadas en esa zona. Sí podés cambiar la dirección y las fotos.',
  STORAGE_PHOTO_HEIC_NOT_SUPPORTED: 'Las fotos HEIC no se ven en el navegador. Exportala como JPG, PNG o WEBP y subila de nuevo.',
  STORAGE_LOCATION_PHOTOS_TOO_MANY: 'Superaste la cantidad máxima de fotos por depósito. Quitá alguna y guardá de nuevo.',
  STORAGE_LOCATION_PHOTO_TOO_LARGE: 'La foto pesa más de lo permitido. Elegí una más liviana.',
  // Audit S6 — `storage-location.validators.ts` (a photo entry that is not a valid stored photo),
  // `request-location-photo-upload-url.handler.ts` (a type off the list) and the schemas' length cap.
  STORAGE_LOCATION_PHOTOS_INVALID: 'Una de las fotos no se cargó bien. Quitala, subila de nuevo y guardá.',
  VALIDATION_ENUM_INVALID: 'Ese formato de foto no está permitido. Subí un JPG, PNG o WEBP.',
  VALIDATION_FIELD_TOO_LONG: 'Uno de los datos es demasiado largo (por ejemplo, la dirección). Acortalo y guardá de nuevo.',
  // NEX-1168 — `storage-location.validators.ts`'s `sanitizeCoordinates`: the coordinates of the
  // picked address are missing one half or out of range.
  STORAGE_LOCATION_COORDINATES_INVALID:
    'No pudimos guardar la ubicación de esa dirección en el mapa. Elegila de nuevo entre las sugerencias, o escribila a mano y guardá.',
  // Audit CTR-7 — `POST /storage/locations/photos/upload` without a file (`storage.controller.ts`)
  // and multer refusing the multipart (`shared/single-file-upload.ts`: a second file, a wrong field).
  UPLOAD_FILE_REQUIRED: 'Elegí un archivo antes de subir.',
  UPLOAD_FILE_INVALID: 'No pudimos leer el archivo. Probá con otro.',
  // Audit CTR-3 — the module closed after the page loaded (`require-storage-module.middleware.ts`).
  STORAGE_MODULE_DISABLED: STORAGE_MODULE_OFF_MESSAGE,
  [UPLOAD_NETWORK_ERROR_CODE]: UPLOAD_NETWORK_ERROR_MESSAGE,
  CONTRACT_VIOLATION: 'No pudimos procesar la respuesta del servidor. Intentá de nuevo en unos minutos.'
};

/**
 * NEX-1165 — the Spanish message of a code this module knows, `null` for anything else. Also a 413:
 * the web server in front of the API answers it (an HTML page, no code) to a body over its own
 * limit, which may sit below the API's 10 MB per photo.
 */
export function knownApiErrorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const known = error.code ? LOCATION_ERROR_MESSAGES[error.code] : undefined;
  if (known) return known;
  return error.status === 413 ? 'La foto es demasiado pesada para subirla. Elegí una más liviana.' : null;
}

/**
 * `storage_locations` (nexvia-api-web migration `1788800000000-CreateStorageLocations.js`):
 * `department`/`locality` are plain name strings (not ids), same convention already used by
 * `drivers.department`/`drivers.locality` — not a new idea introduced here.
 *
 * Derived from `src/contracts/storage-locations.ts`'s `StorageLocationSchema` (Phase 3.2 of the
 * harness roadmap) instead of hand-written, so this type and the runtime check below can never
 * drift apart the way the old standalone interface could.
 */
export type StorageLocation = StorageLocationContract;

/**
 * NEX-1168 (plan 2026-10-20 Tarea 2.7) — where the address is, from the address autocomplete. Both
 * coordinates or neither (the API answers 400 `STORAGE_LOCATION_COORDINATES_INVALID` to one alone);
 * `placeId` only means something next to them.
 */
export interface StorageLocationCoordinates {
  latitude: number;
  longitude: number;
  placeId: string | null;
}

/**
 * The body of `POST`/`PATCH /storage/locations`. The coordinates are left out for an address typed
 * by hand: on a `PATCH` the API then keeps the stored ones if the address did not change, and clears
 * them if it did (they belonged to the old address).
 */
export type StorageLocationInput = Pick<StorageLocation, 'department' | 'locality' | 'address' | 'photos'> &
  Partial<StorageLocationCoordinates>;

const LOCATIONS_PATH = '/storage/locations';

/**
 * A 2xx response that fails `StorageLocationSchema`/`StorageLocationListSchema` (a malformed or
 * unexpected real response — the contract the backend and this module agreed on, broken) is
 * never allowed to escape as a raw `ZodError`: that would crash the calling page exactly the way
 * `(current ?? []) is not iterable` did before this phase. Re-thrown as this module's own
 * `ApiError` instead, with a stable `CONTRACT_VIOLATION` code, so the existing page-level
 * `ApiError` handling (`describeApiError`) degrades to the existing error banner.
 */
function parseContract<T>(schema: { parse: (input: unknown) => T }, body: unknown): T {
  try {
    return schema.parse(body);
  } catch (error) {
    if (error instanceof ZodError) {
      throw new ApiError('Storage location response did not match the expected contract', 502, 'CONTRACT_VIOLATION');
    }
    throw error;
  }
}

export async function listStorageLocations(): Promise<StorageLocation[]> {
  const body = await apiGet<unknown>(LOCATIONS_PATH);
  return parseContract(StorageLocationListSchema, body);
}

export async function createStorageLocation(input: StorageLocationInput): Promise<StorageLocation> {
  const body = await apiPost<unknown>(LOCATIONS_PATH, input);
  return parseContract(StorageLocationSchema, body);
}

export async function updateStorageLocation(id: number, input: StorageLocationInput): Promise<StorageLocation> {
  const body = await apiPatch<unknown>(`${LOCATIONS_PATH}/${id}`, input);
  return parseContract(StorageLocationSchema, body);
}

export function deleteStorageLocation(id: number): Promise<void> {
  return apiDelete<void>(`${LOCATIONS_PATH}/${id}`);
}

// --- Location photos (NEX-1164 fix) ----------------------------------------------------------
//
// Replaces `LocationForm.tsx`'s old `readPhotoAsDataUrl`: a photo used to be read client-side as a
// base64 data URL and embedded directly in the `POST`/`PATCH /storage/locations` JSON body. A real
// photo (even comfortably under this form's own 10 MB client-side cap) is ~33% larger as base64,
// which blew past `express.json()`'s default 100 KB body-size limit on nexvia-api-web before any of
// its own validation ever ran — attaching a photo to a depósito did not work at all. Fixed by
// mirroring `src/pages/empresa/storage-api.ts`'s own presigned-upload shape for documents
// (then `requestDocumentUploadUrl` + `uploadFileToPresignedUrl`) for location photos.
//
// 2026-10-28 — the presigned PUT straight to Spaces was refused by the bucket's CORS from the
// panel's domain in every environment ("No pudimos subir el archivo"). The photo now goes to
// `POST /storage/locations/photos/upload` and the API puts it in Spaces itself, like the documents
// (`uploadDocument` in `src/pages/empresa/storage-api.ts`).

/**
 * One location photo: the API stores it and answers its durable, already-public URL to keep in
 * `photos[]`. There is no `confirm` afterward — `storage_locations.photos` has no per-photo review
 * state (see the API handler's docblock for the ACL reasoning).
 */
export async function uploadStorageLocationPhoto(file: File): Promise<string> {
  const body = new FormData();
  body.append('file', file);
  try {
    const { photoUrl } = await request<{ photoUrl: string }>(`${LOCATIONS_PATH}/photos/upload`, { method: 'POST', body });
    return photoUrl;
  } catch (err) {
    // NEX-1165 — fetch rejects (a bare TypeError "Failed to fetch") when the request gets no answer
    // at all. Coded so the page shows Spanish copy.
    if (err instanceof TypeError) throw new ApiError(UPLOAD_NETWORK_ERROR_MESSAGE, 0, UPLOAD_NETWORK_ERROR_CODE);
    throw err;
  }
}
